import type { ApiErrorParams } from '@/lib/api/client'

import { formatVND, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { contentLinkPlaces } from '@/lib/content-link-places'
import { shopInvalidFieldMessage } from '@/lib/shop-field-invalid'
import {
    badgeFieldMessage,
    designFieldMessage,
} from '@/lib/product-image-design/design-error-message'

// The frontend is the single owner of error i18n: backend emits
// `{ code, params?, details? }` and this module is the one place that turns a
// backend error code into Vietnamese copy. See
// docs/decisions/error-i18n-single-owner.md.
//
// Keys are exact backend codes. A code missing here resolves to null so the
// caller can keep the transition behaviour until the backend module is
// migrated; add the Paraglide message and the mapping in the same change.
//
// `required` names the params a message needs to read as a sentence. When any
// required param is missing or empty, the resolver returns null instead of
// rendering a broken sentence like "Viettel Post từ chối yêu cầu: ".
//
// Codes whose wording depends on request context (e.g. a generic CONFLICT that
// only means "duplicate code" for one module) stay overridden at the caller and
// must not be added here.
interface MessageEntry {
    required?: readonly string[]
    build: (params: ApiErrorParams) => string
}

const API_ERROR_MESSAGES: Record<string, MessageEntry> = {
    // Storefront API (server web bán hàng gọi bằng API key): web tự dịch câu
    // cho khách; ở ERP các mã này chỉ hiện khi nhân viên thử API bằng tay.
    FORBIDDEN_SCOPE: {
        build: () => m.error_forbidden_scope(),
    },
    // Partner API: khoá partner gọi API chưa được cấp quyền (M-182).
    FORBIDDEN_PERMISSION: {
        build: () => m.error_forbidden_permission(),
    },
    IDEMPOTENCY_KEY_REQUIRED: {
        build: () => m.error_idempotency_key_required(),
    },
    RATE_LIMITED: {
        build: () => m.error_rate_limited(),
    },
    AUTH_REFRESH_RACE: {
        build: () => m.error_auth_refresh_race(),
    },
    SHIPPING_WEIGHT_MISSING: {
        build: () => m.error_shipping_weight_missing(),
    },
    SHIPPING_ADDRESS_UNSUPPORTED: {
        build: () => m.error_shipping_address_unsupported(),
    },
    SHIPPING_NO_SERVICE: {
        build: () => m.error_shipping_no_service(),
    },
    SHIPPING_QUOTE_UNAVAILABLE: {
        build: () => m.error_shipping_quote_unavailable(),
    },
    ONLINE_PRICE_INVALID: {
        required: ['max'],
        build: (params) =>
            m.error_online_price_invalid({
                max: formatVND(Number(params.max)),
            }),
    },
    // Luật tên khách chung N5 (1–120 ký tự).
    CUSTOMER_NAME_INVALID: {
        required: ['reason'],
        build: (params) =>
            params.reason === 'too_long'
                ? m.customer_name_too_long({ max: String(params.max ?? '120') })
                : m.validation_required_name(),
    },
    // Tài khoản khách bán lẻ (màn Bán lẻ › Tài khoản khách + API phiên khách).
    SESSION_EXPIRED: {
        build: () => m.error_shop_session_expired(),
    },
    CHANNEL_NOT_ALLOWED: {
        build: () => m.error_shop_channel_not_allowed(),
    },
    SHOP_ACCOUNT_NOT_FOUND: {
        build: () => m.error_shop_account_not_found(),
    },
    // Gắn khách ERP cho tài khoản online (1 khách ↔ 1 tài khoản, cùng SĐT đã xác minh).
    CUSTOMER_ALREADY_LINKED: {
        build: () => m.error_shop_customer_already_linked(),
    },
    CUSTOMER_PHONE_MISMATCH: {
        build: () => m.error_shop_customer_phone_mismatch(),
    },
    ACCOUNT_LOCKED: {
        build: () => m.error_shop_account_locked(),
    },
    // Đơn bán lẻ (shop API): trần COD chờ duyệt, Mini App chưa xác minh SĐT.
    // Huỷ và tạo lại đơn quà web (§14.21) — sẽ mất lời nhắn / ẩn giá / ship trả trước.
    ORDER_RECREATE_GIFT: {
        build: () => m.error_order_recreate_gift(),
    },
    // Sao chép đơn (M-187): loại đơn không chép được (BE chặn, FE cũng làm mờ nút).
    ORDER_DUPLICATE_COUNTER_SALE: {
        build: () => m.order_duplicate_disabled_quick_sale(),
    },
    ORDER_DUPLICATE_GIFT: {
        build: () => m.order_duplicate_disabled_gift(),
    },
    ORDER_DUPLICATE_KIOTVIET: {
        build: () => m.order_duplicate_disabled_kiotviet(),
    },
    ORDER_LIMIT_REACHED: {
        build: () => m.error_shop_order_limit_reached(),
    },
    PHONE_REQUIRED: {
        build: () => m.error_shop_phone_required(),
    },
    // Danh mục bán + nhóm sản phẩm bán lẻ.
    SHOP_FIELD_INVALID: {
        required: ['reason'],
        build: (params) => {
            switch (params.reason) {
                case 'required':
                    // Ô chọn cấp OCOP: nói cần chọn gì, không "nhập ô này".
                    return params.field === 'ocop.level'
                        ? m.error_shop_ocop_level_required()
                        : m.error_shop_field_required()
                case 'too_long':
                    return m.error_shop_field_too_long({
                        max: String(params.max ?? ''),
                    })
                case 'too_many':
                    return m.error_shop_field_too_many({
                        max: String(params.max ?? ''),
                    })
                default:
                    return shopInvalidFieldMessage(
                        String(params.field ?? ''),
                        String(params.max ?? ''),
                    )
            }
        },
    },
    // Báo cáo theo khoảng ngày: Từ ngày sau Đến ngày (ERP-11).
    DATE_RANGE_REVERSED: {
        build: () => m.error_date_range_reversed(),
    },
    // Báo cáo theo kỳ (M-45): group_by ngoài week/month/year, area_id sai UUID.
    PERIOD_GROUP_BY_INVALID: {
        build: () => m.error_period_group_by_invalid(),
    },
    CARE_LOG_USAGE_AREA_INVALID: {
        build: () => m.error_care_log_usage_area_invalid(),
    },
    // Danh mục sản phẩm (ERP-10).
    PRODUCT_CATEGORY_CODE_INVALID: {
        required: ['max'],
        build: (params) =>
            m.product_category_code_format({ max: String(params.max) }),
    },
    PRODUCT_CATEGORY_CODE_TAKEN: {
        build: () => m.product_category_code_taken(),
    },
    // Tạo API key (ERP-06): mỗi nguyên nhân một mã.
    API_KEY_NAME_REQUIRED: {
        build: () => m.api_keys_name_required(),
    },
    API_KEY_NAME_TOO_LONG: {
        required: ['max'],
        build: (params) =>
            m.api_keys_name_too_long({ max: String(params.max) }),
    },
    API_KEY_SCOPE_INVALID: {
        build: () => m.api_keys_scope_invalid(),
    },
    // Phân quyền khoá partner (M-182).
    API_KEY_PERMISSIONS_REQUIRED: {
        build: () => m.api_keys_permissions_required(),
    },
    API_KEY_PERMISSIONS_INVALID: {
        build: () => m.api_keys_permissions_invalid(),
    },
    API_KEY_NOT_EDITABLE: {
        build: () => m.api_keys_not_editable(),
    },
    SHOP_SLUG_TAKEN: {
        required: ['slug'],
        build: (params) =>
            m.error_shop_slug_taken({ slug: String(params.slug) }),
    },
    SHOP_VARIANT_IN_OTHER_GROUP: {
        required: ['sku', 'group'],
        build: (params) =>
            m.error_shop_variant_in_other_group({
                sku: String(params.sku),
                group: String(params.group),
            }),
    },
    SHOP_VARIANT_DUPLICATE: {
        build: () => m.error_shop_variant_duplicate(),
    },
    SHOP_CATEGORY_IN_USE: {
        required: ['count'],
        build: (params) =>
            m.error_shop_category_in_use({ count: String(params.count) }),
    },
    // ERP-08: đích link (danh mục / nhóm) hoặc file ảnh không còn.
    SHOP_TARGET_MISSING: {
        required: ['kind'],
        build: (params) => {
            const slug = String(params.value ?? '')
            switch (params.kind) {
                case 'category':
                    return m.error_shop_target_category_missing({ slug })
                case 'group':
                    return m.error_shop_target_group_missing({ slug })
                case 'post':
                    return m.cmsPost_error_target_missing({ slug })
                case 'page':
                    return m.cmsPage_error_target_page({ slug })
                case 'story':
                    return m.cmsPage_error_target_story({ slug })
                default:
                    return m.error_shop_target_file_missing()
            }
        },
    },
    // Bài viết / chuyên mục tin tức (CMS về ERP, E3).
    SHOP_POST_CATEGORY_IN_USE: {
        required: ['count'],
        build: (params) =>
            m.cmsPost_error_category_in_use({ count: String(params.count) }),
    },
    SHOP_CONTENT_LINKED: {
        required: ['where'],
        build: (params) =>
            m.cmsPost_error_linked({
                where: contentLinkPlaces(String(params.where)),
            }),
    },
    SHOP_POST_NOT_FOUND: {
        build: () => m.cmsPost_not_found(),
    },
    SHOP_REVISION_NOT_FOUND: {
        build: () => m.cmsTools_error_revision_not_found(),
    },
    SHOP_PREVIEW_WEB_URL_MISSING: {
        build: () => m.cmsTools_error_web_url_missing(),
    },
    SHOP_PAGE_NOT_FOUND: {
        build: () => m.cmsPage_not_found(),
    },
    SHOP_STORY_NOT_FOUND: {
        build: () => m.cmsPage_error_story_not_found(),
    },
    SHOP_PAGE_SLUG_RESERVED: {
        required: ['slug'],
        build: (params) =>
            m.cmsPage_error_slug_reserved_server({ slug: String(params.slug) }),
    },
    SHOP_POST_CATEGORY_NOT_FOUND: {
        build: () => m.cmsPost_error_category_not_found(),
    },
    SHOP_REF_MISSING: {
        build: () => m.error_shop_ref_missing(),
    },
    SHOP_CONCURRENT_CHANGE: {
        build: () => m.error_shop_concurrent_change(),
    },
    SHOP_CATEGORY_NOT_FOUND: {
        build: () => m.error_shop_category_not_found(),
    },
    GROUP_NOT_FOUND: {
        build: () => m.error_shop_group_not_found(),
    },
    SHOP_IMAGE_TYPE: {
        build: () => m.error_shop_image_type(),
    },
    SHOP_IMAGE_REJECTED: {
        build: () => m.error_shop_image_rejected(),
    },
    // Thư viện ảnh bán lẻ (Bán lẻ › Thư viện ảnh, MediaPicker).
    SHOP_MEDIA_TYPE: {
        build: () => m.media_error_type(),
    },
    SHOP_MEDIA_TOO_LARGE: {
        required: ['max_mb'],
        build: (params) =>
            m.media_error_too_large({ max: String(params.max_mb) }),
    },
    SHOP_MEDIA_REJECTED: {
        build: () => m.media_error_rejected(),
    },
    SHOP_MEDIA_FILES_REQUIRED: {
        build: () => m.media_error_files_required(),
    },
    SHOP_MEDIA_TOO_MANY: {
        required: ['max'],
        build: (params) => m.media_upload_too_many({ max: String(params.max) }),
    },
    SHOP_MEDIA_IN_USE: {
        required: ['count'],
        build: (params) =>
            m.media_error_in_use({ count: String(params.count) }),
    },
    SHOP_MEDIA_NOT_FOUND: {
        build: () => m.media_error_not_found(),
    },
    // Video hướng dẫn sử dụng (Bán lẻ › Video hướng dẫn sử dụng).
    SHOP_GUIDE_VIDEO_NOT_FOUND: {
        build: () => m.error_shop_guide_video_not_found(),
    },
    SHOP_GUIDE_VIDEO_MEDIA_MISSING: {
        build: () => m.error_shop_guide_video_media_missing(),
    },
    SHOP_GUIDE_VIDEO_NOT_VIDEO: {
        build: () => m.error_shop_guide_video_not_video(),
    },
    SHOP_GUIDE_VIDEO_POSTER_NOT_IMAGE: {
        build: () => m.error_shop_guide_video_poster_not_image(),
    },
    SHOP_GUIDE_VIDEO_ORDER_REQUIRED: {
        build: () => m.error_shop_guide_video_order_required(),
    },
    // Thiết kế ảnh sản phẩm + thư viện logo chứng nhận (usecase/productimagedesign).
    PRODUCT_IMAGE_DESIGN_FIELD_INVALID: {
        required: ['field'],
        build: (params) => designFieldMessage(params),
    },
    PRODUCT_IMAGE_DESIGN_BADGE_MISSING: {
        build: () => m.error_product_image_design_badge_missing(),
    },
    PRODUCT_IMAGE_FILE_TYPE: {
        required: ['allowed'],
        build: (params) =>
            m.error_product_image_file_type({
                allowed: String(params.allowed).toUpperCase(),
            }),
    },
    PRODUCT_IMAGE_FILE_TOO_LARGE: {
        required: ['max_mb'],
        build: (params) =>
            m.error_product_image_file_too_large({
                max: String(params.max_mb),
            }),
    },
    PRODUCT_IMAGE_DIMENSIONS: {
        required: ['max_px'],
        build: (params) =>
            m.error_product_image_dimensions({ max: String(params.max_px) }),
    },
    PRODUCT_IMAGE_EXPORT_SIZE: {
        required: ['size', 'width', 'height'],
        build: (params) =>
            m.error_product_image_export_size({
                size: String(params.size),
                width: String(params.width),
                height: String(params.height),
            }),
    },
    PRODUCT_NOT_FOUND: {
        build: () => m.error_product_not_found(),
    },
    FILE_REQUIRED: {
        build: () => m.error_file_required(),
    },
    CERTIFICATION_BADGE_FIELD_INVALID: {
        required: ['field'],
        build: (params) => badgeFieldMessage(params),
    },
    CERTIFICATION_BADGE_NAME_TAKEN: {
        required: ['name'],
        build: (params) =>
            m.error_certification_badge_name_taken({
                name: String(params.name),
            }),
    },
    CERTIFICATION_BADGE_IN_USE: {
        required: ['count'],
        build: (params) =>
            m.error_certification_badge_in_use({ count: String(params.count) }),
    },
    CERTIFICATION_BADGE_NOT_FOUND: {
        build: () => m.error_certification_badge_not_found(),
    },
    // Hạng thành viên bán lẻ (màn Bán lẻ › Hạng thành viên).
    SHOP_TIER_NAME_INVALID: {
        required: ['max'],
        build: (params) =>
            m.error_shop_tier_name_invalid({ max: String(params.max) }),
    },
    SHOP_TIER_NAME_DUPLICATE: {
        required: ['name'],
        build: (params) =>
            m.error_shop_tier_name_duplicate({ name: String(params.name) }),
    },
    SHOP_TIER_MIN_SPEND_INVALID: {
        required: ['max'],
        build: (params) =>
            m.error_shop_tier_min_spend_invalid({
                max: formatVnd(Number(params.max)),
            }),
    },
    SHOP_TIER_MIN_SPEND_DUPLICATE: {
        required: ['name', 'amount'],
        build: (params) =>
            m.error_shop_tier_min_spend_duplicate({
                name: String(params.name),
                amount: formatVnd(Number(params.amount)),
            }),
    },
    SHOP_TIER_DISCOUNT_INVALID: {
        required: ['max'],
        build: (params) =>
            m.error_shop_tier_discount_invalid({ max: String(params.max) }),
    },
    SHOP_TIER_CONFLICT: {
        build: () => m.error_shop_tier_conflict(),
    },
    SHOP_TIER_NOT_FOUND: {
        build: () => m.error_shop_tier_not_found(),
    },
    // M-145: đơn KiotViet còn dòng chưa nối SP → không xuất kho được.
    KIOTVIET_ITEMS_UNLINKED: {
        build: (params) => {
            if (!hasValue(params.count))
                return m.error_kiotviet_items_unlinked_generic()
            const count = String(params.count)
            // Dòng không mã KiotViet: server gửi count nhưng codes rỗng.
            return hasValue(params.codes)
                ? m.error_kiotviet_items_unlinked({
                      count,
                      codes: String(params.codes),
                  })
                : m.error_kiotviet_items_unlinked_count({ count })
        },
    },
    SYSTEM_PRODUCT_LOCKED: {
        build: () => m.error_system_product_locked(),
    },
    // Mã vạch chỉ được giữ bởi một SP đang hoạt động (M-156). Có params thì
    // nêu SP đang giữ mã để người dùng biết gỡ ở đâu.
    BARCODE_EXISTS: {
        build: (params) =>
            hasValue(params.sku) && hasValue(params.name)
                ? m.error_barcode_exists({
                      sku: toText(params.sku),
                      name: toText(params.name),
                  })
                : m.error_barcode_exists_unknown(),
    },
    // Mã hàng duy nhất cả bảng, kể cả SP ngừng hoạt động (ẩn khỏi danh sách
    // mặc định) nên nói rõ SP đó có đang hoạt động không.
    SKU_EXISTS: {
        build: (params) => {
            if (!hasValue(params.sku) || !hasValue(params.name)) {
                return m.error_sku_exists_unknown()
            }
            const values = {
                sku: toText(params.sku),
                name: toText(params.name),
            }
            return toText(params.active) === 'false'
                ? m.error_sku_exists_inactive(values)
                : m.error_sku_exists_active(values)
        },
    },
    INVALID_BARCODE_HOLDER: {
        build: () => m.error_invalid_barcode_holder(),
    },
    BARCODE_HOLDER_DEACTIVATE_FORBIDDEN: {
        build: () => m.error_barcode_holder_deactivate_forbidden(),
    },
    INVALID_PRODUCT_CATEGORY: {
        build: () => m.error_invalid_product_category(),
    },
    // Công thức combo (M-156): mỗi nguyên nhân một câu thay cho câu kỹ thuật.
    COMBO_PARENT_NOT_FOUND: {
        build: () => m.error_combo_parent_not_found(),
    },
    COMBO_COMPONENT_NOT_FOUND: {
        build: () => m.error_combo_component_not_found(),
    },
    COMBO_PARENT_NOT_COMBO: {
        build: () => m.error_combo_parent_not_combo(),
    },
    COMBO_COMPONENT_SELF: {
        build: () => m.error_combo_component_self(),
    },
    COMBO_COMPONENT_CYCLE: {
        build: () => m.error_combo_component_cycle(),
    },
    COMBO_INVALID_QUANTITY: {
        build: () => m.error_combo_invalid_quantity(),
    },
    COMBO_INVALID_ITEMS: {
        build: () => m.error_combo_invalid_items(),
    },
    COMBO_DUPLICATE_COMPONENT: {
        build: () => m.error_combo_duplicate_component(),
    },
    LOT_NOT_FOR_PRODUCT: {
        build: () => m.error_lot_not_for_product(),
    },
    KIOTVIET_LINK_ORDER_STOCKED_OUT: {
        build: () => m.error_kiotviet_link_order_stocked_out(),
    },
    KIOTVIET_LINK_NOT_KIOTVIET_ORDER: {
        build: () => m.error_kiotviet_link_not_kiotviet_order(),
    },
    KIOTVIET_LINK_ITEM_NOT_FOUND: {
        build: () => m.error_kiotviet_link_item_not_found(),
    },
    KIOTVIET_LINK_ITEM_NO_CODE: {
        build: () => m.error_kiotviet_link_item_no_code(),
    },
    KIOTVIET_LINK_INVALID_PRODUCT: {
        build: () => m.error_kiotviet_link_invalid_product(),
    },
    KIOTVIET_INVOICE_NOT_FOUND: {
        build: () => m.error_kiotviet_invoice_not_found(),
    },
    KIOTVIET_INVOICE_DATE_INVALID: {
        build: () => m.error_kiotviet_invoice_date_invalid(),
    },
    KIOTVIET_UNAVAILABLE: {
        build: () => m.error_kiotviet_unavailable(),
    },
    OVERPAYMENT: {
        required: ['remaining_amount'],
        build: (params) =>
            m.error_overpayment({
                remaining: formatVND(Number(params.remaining_amount)),
            }),
    },
    // Đổi hình thức khoản thu trên đơn (M-152). Cùng mã cũng là
    // `method_change_block` trong lịch sử thanh toán của đơn.
    PAYMENT_NOT_FOUND: { build: () => m.error_payment_not_found() },
    PAYMENT_METHOD_UNCHANGED: {
        build: () => m.error_payment_method_unchanged(),
    },
    PAYMENT_METHOD_CHANGE_UNSUPPORTED: {
        build: () => m.error_payment_method_change_unsupported(),
    },
    PAYMENT_METHOD_CHANGE_CREDIT: {
        build: () => m.error_payment_method_change_credit(),
    },
    PAYMENT_METHOD_CHANGE_CARRIER_COD: {
        build: () => m.error_payment_method_change_carrier_cod(),
    },
    PAYMENT_METHOD_CHANGE_KIOTVIET: {
        build: () => m.error_payment_method_change_kiotviet(),
    },
    PAYMENT_METHOD_CHANGE_SHIPPING_FEE: {
        build: () => m.error_payment_method_change_shipping_fee(),
    },
    PAYMENT_METHOD_CHANGE_BANK_MATCHED: {
        build: () => m.error_payment_method_change_bank_matched(),
    },
    PAYMENT_METHOD_CHANGE_RECEIPT_KIOTVIET: {
        build: () => m.error_payment_method_change_receipt_kiotviet(),
    },
    PAYMENT_RECEIPT_NOT_FOUND: {
        build: () => m.error_payment_receipt_not_found(),
    },
    PAYMENT_METHOD_CHANGE_UNTRACEABLE: {
        build: () => m.error_payment_method_change_untraceable(),
    },
    ONLINE_PRICE_PRODUCT_NOT_FOUND: {
        build: () => m.error_online_price_product_not_found(),
    },
    SLA_REGION_WITHOUT_CARRIER: {
        build: () => m.error_sla_region_without_carrier(),
    },
    SHIPPING_CARRIER_NOT_FOUND: {
        build: () => m.error_shipping_carrier_not_found(),
    },
    CARRIER_SHIPMENT_NOT_FOUND: {
        build: () => m.error_carrier_shipment_not_found(),
    },
    DESK_SSO_DISABLED: {
        build: () => m.error_desk_sso_disabled(),
    },
    DESK_SSO_INVALID_REF: {
        build: () => m.error_desk_sso_invalid_ref(),
    },
    EINVOICE_DRAFT_LINES_REQUIRED: {
        build: () => m.einvoice_draft_lines_required(),
    },
    EINVOICE_DRAFT_REMOVED_LINE_INVALID: {
        build: () => m.einvoice_draft_removed_line_invalid(),
    },
    EINVOICE_CREDENTIAL_MISSING: {
        build: () => m.einvoice_credential_missing_title(),
    },
    EINVOICE_CREDENTIAL_INVALID: {
        build: () => m.einvoice_credential_blocked_title(),
    },
    EINVOICE_PROVIDER_AUTH_FAILED: {
        build: () => m.einvoice_error_auth_failed(),
    },
    EINVOICE_PROVIDER_RATE_LIMITED: {
        build: () => m.einvoice_error_provider_rate_limited(),
    },
    EINVOICE_PROVIDER_UNAVAILABLE: {
        build: () => m.einvoice_error_provider_unavailable(),
    },
    EINVOICE_PROVIDER_TIMEOUT: {
        build: () => m.einvoice_error_provider_timeout(),
    },
    // MISA has not answered a publication at all: the same sentence as the
    // panel. EINVOICE_PUBLICATION_PENDING stays unmapped on purpose: it carries
    // the code MISA did answer with, which staff must still see.
    EINVOICE_PUBLICATION_AWAITING_MISA: {
        build: () => m.einvoice_publish_unknown(),
    },
    EINVOICE_QUICK_SALE_PUBLISH_UNAVAILABLE: {
        build: () => m.einvoice_error_quick_sale_publish_unavailable(),
    },
    EINVOICE_MANUAL_ISSUE_CHECK_REQUIRED: {
        build: () => m.einvoice_republish_check_required(),
    },
    EINVOICE_PUBLICATION_DUPLICATED: {
        build: () => m.einvoice_publish_duplicated(),
    },
    EINVOICE_SERIES_REQUIRED: {
        build: () => m.einvoice_err_series_required(),
    },
    EINVOICE_NUMBER_INVALID: {
        build: () => m.einvoice_err_number_invalid(),
    },
    EINVOICE_DATE_INVALID: { build: () => m.einvoice_err_date_invalid() },
    EINVOICE_DATE_IN_FUTURE: {
        build: () => m.einvoice_err_date_in_future(),
    },
    EINVOICE_NOTE_REQUIRED: { build: () => m.einvoice_err_note_required() },
    EINVOICE_NOTE_TOO_LONG: { build: () => m.einvoice_err_note_too_long() },
    EINVOICE_SKIP_REASON_INVALID: {
        build: () => m.einvoice_err_skip_reason_invalid(),
    },
    EINVOICE_SOURCE_INVALID: {
        build: () => m.einvoice_err_resolution_failed(),
    },
    EINVOICE_SUGGESTION_INVALID: {
        build: () => m.einvoice_err_resolution_failed(),
    },
    EINVOICE_NUMBER_IN_USE: {
        required: ['order_code'],
        build: (params) =>
            m.einvoice_err_number_in_use({
                order_code: String(params.order_code),
            }),
    },
    EINVOICE_NOT_RESOLVABLE: {
        build: () => m.einvoice_err_not_resolvable(),
    },
    EINVOICE_STATUS_CHANGED: {
        build: () => m.einvoice_err_status_changed(),
    },
    EINVOICE_ALREADY_ISSUED_ON_MISA: {
        build: () => m.einvoice_err_already_issued_on_misa(),
    },
    ORDER_NOT_DELIVERED: {
        build: () => m.einvoice_err_order_not_delivered(),
    },
    VNETGPS_RATE_LIMITED: {
        build: () => m.vehicle_camera_live_rate_limited(),
    },
    // New code (split from GROWER_CAMERA_UPSTREAM): merges unauthorized,
    // forbidden, not-found, invalid-token and bad-request into one
    // kind=config alert. The neutral wording matches the in-app notification
    // — no error code, no upstream jargon.
    GROWER_CAMERA_CONFIG: {
        build: () => m.error_grower_camera_config(),
    },
    EINVOICE_PROVIDER_HTTP_ERROR: {
        build: () => m.einvoice_error_provider_http(),
    },
    EINVOICE_PDF_PROVIDER_UNAVAILABLE: {
        build: () => m.einvoice_error_pdf_unavailable(),
    },
    EINVOICE_PDF_RESPONSE_INVALID: {
        build: () => m.einvoice_error_pdf_response_invalid(),
    },
    EINVOICE_PDF_INVALID: { build: () => m.einvoice_error_pdf_invalid() },
    EINVOICE_PREVIEW_PROVIDER_UNAVAILABLE: {
        build: () => m.einvoice_error_preview_unavailable(),
    },
    EINVOICE_PREVIEW_RESPONSE_INVALID: {
        build: () => m.einvoice_error_preview_response_invalid(),
    },
    EINVOICE_PREVIEW_PROVIDER_REJECTED: {
        build: () => m.einvoice_error_preview_rejected(),
    },
    EINVOICE_PREVIEW_URL_REJECTED: {
        build: () => m.einvoice_error_preview_url_rejected(),
    },
    EINVOICE_PREVIEW_REQUEST_INVALID: {
        build: () => m.einvoice_error_preview_request_invalid(),
    },
    EINVOICE_PREVIEW_DOWNLOAD_FAILED: {
        build: () => m.einvoice_error_preview_download_failed(),
    },
    EINVOICE_PREVIEW_PDF_INVALID: {
        build: () => m.einvoice_error_preview_pdf_invalid(),
    },
    INVALID_USERNAME: { build: () => m.user_err_invalid_username() },
    WEAK_PASSWORD: { build: () => m.user_err_weak_password() },
    DUPLICATE_USERNAME: { build: () => m.user_err_duplicate_username() },
    CANNOT_DROP_OWN_ADMIN_ROLE: {
        build: () => m.user_err_cannot_drop_own_admin(),
    },
    CARRIER_CONFIG_ERROR: { build: () => m.carrier_form_config_error() },
    LEGACY_UNIT_REQUIRED: { build: () => m.carrier_legacy_required() },
    LEGACY_UNIT_NOT_CANDIDATE: {
        build: () => m.carrier_legacy_not_candidate(),
    },
    CARRIER_UNAVAILABLE: { build: () => m.carrier_form_unavailable() },
    LOT_NOT_SELLABLE: { build: () => m.product_lot_err_not_sellable() },
    LOT_DEBT_ON_DEFECT_SHELF: {
        build: () => m.product_lot_err_debt_on_defect_shelf(),
    },
    DEFECT_SHELF_MISSING: { build: () => m.defect_shelf_err_missing() },
    INVALID_DEFECT_ITEM: { build: () => m.defect_shelf_err_invalid_item() },
    INVALID_RETURN_POSITION: { build: () => m.return_position_err_invalid() },
    OVERPAY_CREDIT_NEEDS_CUSTOMER: {
        build: () => m.error_overpay_credit_needs_customer(),
    },
    // M-175: Bán ngay thu thiếu / ghi dư quá phần khách đưa dư.
    QUICK_SALE_DEBT_NEEDS_CUSTOMER: {
        build: () => m.customer_select_for_debt(),
    },
    OVERPAY_CREDIT_EXCEEDS_TENDERED: {
        build: () => m.error_overpay_credit_exceeds_tendered(),
    },
    RETURN_ORDER_NOT_DELIVERED: {
        build: () => m.error_return_order_not_delivered(),
    },
    RETURN_KIOTVIET_NOT_ALLOWED: {
        build: () => m.error_return_kiotviet_not_allowed(),
    },
    RETURN_EMPTY: { build: () => m.sales_return_err_empty() },
    RETURN_QUANTITY_EXCEEDS_DELIVERED: {
        build: () => m.error_return_quantity_exceeds_delivered(),
    },
    RETURN_LOT_EXCEEDS_SHIPPED: {
        build: () => m.error_return_lot_exceeds_shipped(),
    },
    RETURN_DEFECT_EXCEEDS: { build: () => m.error_return_defect_exceeds() },
    RETURN_VIRTUAL_LOT: { build: () => m.error_return_virtual_lot() },
    RETURN_NO_STOCK_OUT: { build: () => m.error_return_no_stock_out() },
    RETURN_FEE_EXCEEDS_VALUE: {
        build: () => m.error_return_fee_exceeds_value(),
    },
    RETURN_FEE_REASON_REQUIRED: {
        build: () => m.error_return_fee_reason_required(),
    },
    RETURN_FEE_EXCEEDS_PAID: {
        build: () => m.error_return_fee_exceeds_paid(),
    },
    ORDER_RETURNED: { build: () => m.error_order_returned() },
    ORDER_HAS_PARTIAL_RETURN: {
        build: () => m.error_order_has_partial_return(),
    },
    INVALID_REFUND: { build: () => m.error_invalid_refund() },
    INVALID_REFUND_AMOUNT: { build: () => m.refund_err_amount_required() },
    INVALID_REFUND_METHOD: { build: () => m.refund_err_method_required() },
    REFUND_PERMISSION_DENIED: {
        build: () => m.error_refund_permission_denied(),
    },
    REFUND_EXCEEDS_REFUNDABLE: {
        build: () => m.error_refund_exceeds_refundable(),
    },
    REFUND_EXCEEDS_CREDIT: { build: () => m.error_refund_exceeds_credit() },
    DEFECT_SHELF_CONFLICT: { build: () => m.defect_shelf_err_conflict() },
    DEFECT_ALLOW_SALE_NEEDS_DEFECT_SHELF: {
        build: () => m.defect_shelf_err_allow_sale_needs_shelf(),
    },
    EXPENSE_CATEGORY_SYSTEM_LOCKED: {
        build: () => m.expense_category_system_locked_hint(),
    },
    EXPENSE_FIELD_LOCKED: {
        build: () => m.expense_sync_amount_readonly(),
    },
    EXPENSE_RULE_NOT_FOUND: {
        build: () => m.expense_rule_empty(),
    },
    EXPENSE_RULE_KEYWORDS_REQUIRED: {
        build: () => m.expense_rule_keywords_required(),
    },
    EXPENSE_RULE_CATEGORY_REQUIRED: {
        build: () => m.validation_required(),
    },
    EXPENSE_CATEGORY_USED_BY_RULE: {
        build: () => m.error_expense_category_used_by_rule(),
    },
    EXPENSE_RULE_KEYWORD_EMPTY: {
        build: () => m.error_expense_rule_keyword_empty(),
    },
    EXPENSE_RULE_REORDER_INCOMPLETE: {
        build: () => m.error_expense_rule_reorder_incomplete(),
    },
    EXPENSE_RULE_VENDOR_NAME_TOO_LONG: {
        build: () => m.error_expense_rule_vendor_name_too_long(),
    },
    EXPENSE_RULE_AMOUNT_RANGE_INVALID: {
        build: () => m.expense_rule_amount_range_invalid(),
    },
    IRRIGATION_SCHEDULE_MODE_INVALID: {
        build: () => m.error_irrigation_schedule_mode_invalid(),
    },
    IRRIGATION_SCHEDULE_DURATION_INVALID: {
        build: () => m.error_irrigation_schedule_duration_invalid(),
    },
    IRRIGATION_SCHEDULE_WEEKDAYS_REQUIRED: {
        build: () => m.error_irrigation_schedule_weekdays_required(),
    },
    IRRIGATION_SCHEDULE_WEEKDAYS_INVALID: {
        build: () => m.error_irrigation_schedule_weekdays_invalid(),
    },
    IRRIGATION_SCHEDULE_START_TIMES_REQUIRED: {
        build: () => m.error_irrigation_schedule_start_times_required(),
    },
    IRRIGATION_SCHEDULE_TIME_INVALID: {
        build: () => m.error_irrigation_schedule_time_invalid(),
    },
    IRRIGATION_SCHEDULE_OVERLAP: {
        build: () => m.error_irrigation_schedule_overlap(),
    },
    IRRIGATION_SCHEDULE_WINDOW_INVALID: {
        build: () => m.error_irrigation_schedule_window_invalid(),
    },
    IRRIGATION_SCHEDULE_EVERY_TOO_SHORT: {
        build: () => m.error_irrigation_schedule_every_too_short(),
    },
    IRRIGATION_SCHEDULE_INVALID_BODY: {
        build: () => m.error_irrigation_schedule_invalid_body(),
    },
    IRRIGATION_SCHEDULE_CONFLICTS_RUN: {
        build: () => m.error_irrigation_schedule_conflicts_run(),
    },
    IRRIGATION_SCHEDULE_FAILED: {
        build: () => m.error_irrigation_schedule_failed(),
    },
    IRRIGATION_RUN_CONFLICT: {
        build: () => m.error_irrigation_run_conflict(),
    },
    IRRIGATION_RUN_DURATION_INVALID: {
        build: () => m.error_irrigation_run_duration_invalid(),
    },
    IRRIGATION_RUN_START_IN_PAST: {
        build: () => m.error_irrigation_run_start_in_past(),
    },
    IRRIGATION_RUN_NOT_FOUND: {
        build: () => m.error_irrigation_run_not_found(),
    },
    IRRIGATION_RUN_NOT_CANCELLABLE: {
        build: () => m.error_irrigation_run_not_cancellable(),
    },
    IRRIGATION_RUN_MISSED: {
        build: () => m.error_irrigation_run_missed(),
    },
    IRRIGATION_PUMP_COOLDOWN: {
        build: () => m.error_irrigation_pump_cooldown(),
    },
    IRRIGATION_NOT_MANUAL_MODE: {
        build: () => m.error_irrigation_not_manual_mode(),
    },
    IRRIGATION_DEVICE_NOT_FOUND: {
        build: () => m.error_irrigation_device_not_found(),
    },
    IRRIGATION_BROKER_DOWN: {
        build: () => m.error_irrigation_broker_down(),
    },
    IRRIGATION_OUTCOME_UNKNOWN: {
        build: () => m.error_irrigation_outcome_unknown(),
    },
    IRRIGATION_DEVICE_ID_REQUIRED: {
        build: () => m.error_irrigation_device_id_required(),
    },
    IRRIGATION_DEVICE_NAME_REQUIRED: {
        build: () => m.error_irrigation_device_name_required(),
    },
    IRRIGATION_DEVICE_NAME_TOO_LONG: {
        build: () => m.error_irrigation_device_name_too_long(),
    },
    IRRIGATION_PUMP_FAILED: {
        build: () => m.error_irrigation_pump_failed(),
    },
    IRRIGATION_DISABLED: {
        build: () => m.error_irrigation_disabled(),
    },
    IRRIGATION_UNAUTHORIZED: {
        build: () => m.error_irrigation_unauthorized(),
    },
    IRRIGATION_UPSTREAM: {
        build: () => m.error_irrigation_upstream(),
    },
    IRRIGATION_DEVICES_FAILED: {
        build: () => m.error_irrigation_devices_failed(),
    },
    SMARTLAB_IRRIGATION_BASE_URL_REQUIRED: {
        build: () => m.smartlab_irrigation_error_base_url_required(),
    },
    SMARTLAB_IRRIGATION_BASE_URL_INVALID: {
        build: () => m.smartlab_irrigation_error_base_url_invalid(),
    },
    SMARTLAB_IRRIGATION_BASE_URL_NOT_HTTPS: {
        build: () => m.smartlab_irrigation_error_base_url_not_https(),
    },
    SMARTLAB_IRRIGATION_USERNAME_REQUIRED: {
        build: () => m.smartlab_irrigation_error_username_required(),
    },
    SMARTLAB_IRRIGATION_PASSWORD_REQUIRED: {
        build: () => m.smartlab_irrigation_error_password_required(),
    },
    SMARTLAB_IRRIGATION_INVALID_BODY: {
        build: () => m.smartlab_irrigation_error_invalid_body(),
    },
    SMARTLAB_IRRIGATION_SETTINGS_FAILED: {
        build: () => m.smartlab_irrigation_error_settings_failed(),
    },
    SMARTLAB_IRRIGATION_ENCRYPTION_UNAVAILABLE: {
        build: () => m.smartlab_irrigation_error_encryption_unavailable(),
    },
    // #M-68: ô chọn túi của sổ đóng gói khác gửi định lượng gam không hợp lệ.
    INVALID_WEIGHT_GRAMS: {
        build: () => m.error_invalid_weight_grams(),
    },
    // Báo cáo chi phí theo danh mục (M-83): mốc biểu đồ sai.
    INVALID_BUCKET: { build: () => m.error_invalid_expense_bucket() },
    // #M-72: sổ đóng hộp gửi số lượng kèm mã lô sai định dạng.
    LEDGER_SNAPSHOT_LOT_INVALID: {
        build: () => m.error_ledger_snapshot_lot_invalid(),
    },
    // #M-69: lưu mẻ sổ kiểm túi có vật tư chưa bật "Kiểm bằng sổ kiểm túi".
    MATERIAL_NOT_COUNT_LOGGED: {
        build: () => m.error_material_not_count_logged(),
    },
    // #M-87: in tem lô trên sổ 12,5g khi phiếu chưa duyệt / dòng không còn.
    PACKAGING_LOG_NOTE_NOT_APPROVED: {
        build: () => m.error_packaging_log_note_not_approved(),
    },
    PACKAGING_LOG_OUTPUT_NOT_FOUND: {
        build: () => m.error_packaging_log_output_not_found(),
    },
    // #M-68: sổ kiểm túi — lượng xuất suy ra lớn hơn tồn các lô của loại túi.
    PACKAGING_COUNT_LOG_INSUFFICIENT_LOT: {
        required: ['materialName', 'requiredQty', 'availableQty'],
        build: (params) =>
            m.error_packaging_count_log_insufficient_lot({
                materialName: toText(params.materialName),
                requiredQty: toText(params.requiredQty),
                availableQty: toText(params.availableQty),
            }),
    },
    // M-139: sửa mẻ sổ kiểm túi đã duyệt mà không có quyền duyệt PXK vật tư;
    // hoặc mẻ vừa bị người khác sửa giữa lúc mở form và lúc lưu.
    PACKAGING_COUNT_LOG_APPROVED_EDIT_FORBIDDEN: {
        build: () => m.error_packaging_count_log_approved_edit_forbidden(),
    },
    PACKAGING_COUNT_LOG_ENTRY_CHANGED: {
        build: () => m.error_packaging_count_log_entry_changed(),
    },
    // #M-74: sổ lấy hương — loại chè sai ô (tấm ↔ thường) hoặc hai dòng trùng
    // loại; và trang sổ bản cũ sửa mẻ đã có nhiều loại / chè tấm.
    SCENTING_LOG_GRADE_KIND_INVALID: {
        build: () => m.error_scenting_log_grade_kind_invalid(),
    },
    SCENTING_LOG_STALE_CLIENT: {
        build: () => m.error_scenting_log_stale_client(),
    },
    // M-150: sổ xưởng — phiếu nhập của mẻ đã duyệt nên dòng sổ khoá; hoặc phiếu
    // chờ duyệt không sửa theo được dòng sổ (cả lần sửa bị huỷ).
    LEDGER_NOTE_APPROVED: {
        build: () => m.error_ledger_note_approved(),
    },
    LEDGER_NOTE_SYNC_FAILED: {
        build: () => m.error_ledger_note_sync_failed(),
    },
    // Quản trị › Zalo ZBS (spec 2026-10-08 §7): kết nối OA, mẫu, mục đích, gửi thử.
    ZALO_NOT_CONFIGURED: {
        required: ['missing'],
        build: (params) =>
            m.error_zalo_not_configured({ missing: toText(params.missing) }),
    },
    ZALO_NOT_CONNECTED: {
        build: () => m.error_zalo_not_connected(),
    },
    ZALO_CONNECTION_FOREIGN: {
        build: () => m.error_zalo_connection_foreign(),
    },
    ZALO_CONNECTION_BROKEN: {
        build: () => m.error_zalo_connection_broken(),
    },
    ZALO_OAUTH_STATE_INVALID: {
        build: () => m.error_zalo_oauth_state_invalid(),
    },
    ZALO_OAUTH_EXCHANGE_FAILED: {
        required: ['zalo_error'],
        build: (params) =>
            m.error_zalo_oauth_exchange_failed({
                zalo_error: toText(params.zalo_error),
            }),
    },
    ZALO_OAUTH_CALLBACK_INVALID: {
        required: ['field'],
        build: (params) =>
            m.error_zalo_oauth_callback_invalid({
                field: toText(params.field),
            }),
    },
    ZALO_REFRESH_FAILED: {
        required: ['zalo_error'],
        build: (params) =>
            m.error_zalo_refresh_failed({
                zalo_error: toText(params.zalo_error),
            }),
    },
    ZALO_TEST_PHONES_TOO_MANY: {
        required: ['max'],
        build: (params) =>
            m.error_zalo_test_phones_too_many({ max: toText(params.max) }),
    },
    ZALO_UNAVAILABLE: {
        build: () => m.error_zalo_unavailable(),
    },
    ZALO_API_ERROR: {
        required: ['zalo_error'],
        build: (params) =>
            m.error_zalo_api_error({ zalo_error: toText(params.zalo_error) }),
    },
    ZNS_TEMPLATE_NOT_FOUND: {
        build: () => m.error_zns_template_not_found(),
    },
    ZNS_TEMPLATE_NOT_ENABLED: {
        build: () => m.error_zns_template_not_enabled(),
    },
    ZNS_TEMPLATE_TAG_MISMATCH: {
        required: ['tag'],
        build: (params) =>
            m.error_zns_template_tag_mismatch({ tag: toText(params.tag) }),
    },
    ZNS_PARAM_UNMAPPED: {
        build: () => m.error_zns_param_unmapped(),
    },
    ZNS_PARAM_TOO_SHORT: {
        required: ['max_length', 'needed'],
        build: (params) =>
            m.error_zns_param_too_short({
                max_length: toText(params.max_length),
                needed: toText(params.needed),
            }),
    },
    ZNS_PARAM_BELOW_MIN_LENGTH: {
        required: ['min_length'],
        build: (params) =>
            m.error_zns_param_below_min_length({
                min_length: toText(params.min_length),
            }),
    },
    ZNS_PARAM_UNKNOWN: {
        build: () => m.error_zns_param_unknown(),
    },
    ZNS_PARAM_SOURCE_INVALID: {
        build: () => m.error_zns_param_source_invalid(),
    },
    ZNS_OTP_PARAM_MISSING: {
        build: () => m.error_zns_otp_param_missing(),
    },
    ZNS_PURPOSE_UNKNOWN: {
        build: () => m.error_zns_purpose_unknown(),
    },
    ZNS_PURPOSE_NOT_AVAILABLE: {
        build: () => m.error_zns_purpose_not_available(),
    },
    ZNS_PURPOSE_NOT_READY: {
        build: () => m.error_zns_purpose_not_ready(),
    },
    ZNS_SEND_REJECTED: {
        required: ['zalo_error'],
        build: (params) =>
            m.error_zns_send_rejected({
                zalo_error: toText(params.zalo_error),
            }),
    },
    ZNS_SEND_UNKNOWN: {
        build: () => m.error_zns_send_unknown(),
    },
    ZNS_SYNC_FAILED: {
        build: () => m.error_zns_sync_failed(),
    },
    ZNS_TEST_PHONE_NOT_ALLOWED: {
        build: () => m.error_zns_test_phone_not_allowed(),
    },
    ZNS_TEST_RATE_LIMITED: {
        required: ['limit'],
        build: (params) =>
            m.error_zns_test_rate_limited({ limit: toText(params.limit) }),
    },
    ZNS_PHONE_INVALID: {
        build: () => m.error_zns_phone_invalid(),
    },
    ZNS_VALUE_MISSING: {
        required: ['source'],
        build: (params) =>
            m.error_zns_value_missing({ source: toText(params.source) }),
    },
    ZNS_MESSAGE_NOT_FOUND: {
        build: () => m.error_zns_message_not_found(),
    },
    ZNS_MESSAGE_NOT_SENT: {
        build: () => m.error_zns_message_not_sent(),
    },
    ZALO_TEST_PHONE_INVALID: {
        required: ['index'],
        build: (params) =>
            m.error_zalo_test_phone_invalid({
                position: String(Number(params.index) + 1),
            }),
    },
    DRIVER_REMITTANCE_NO_PAYMENTS: {
        build: () => m.error_driver_remittance_no_payments(),
    },
    DRIVER_REMITTANCE_INVALID_METHOD: {
        build: () => m.error_driver_remittance_invalid_method(),
    },
    DRIVER_REMITTANCE_PAYMENT_NOT_HELD: {
        build: () => m.error_driver_remittance_payment_not_held(),
    },
    DRIVER_REMITTANCE_SELF_RECEIVE: {
        build: () => m.error_driver_remittance_self_receive(),
    },
    DRIVER_REMITTANCE_RECEIVED_IN_FUTURE: {
        build: () => m.driverRemittance_receive_at_future(),
    },
    CARRIER_REJECTED: {
        required: ['carrierMessage'],
        build: (params) =>
            m.carrier_form_rejected({
                carrierMessage: toText(params.carrierMessage),
            }),
    },
    // Sự kiện lặp lại trên lịch công việc (#M-154).
    WORK_ACTIVITY_INVALID_RECURRENCE: {
        build: () => m.error_work_activity_invalid_recurrence(),
    },
    WORK_ACTIVITY_RECURRENCE_EVENT_ONLY: {
        build: () => m.error_work_activity_recurrence_event_only(),
    },
    WORK_ACTIVITY_INVALID_RECURRENCE_UNTIL: {
        build: () => m.error_work_activity_invalid_recurrence_until(),
    },
    WORK_ACTIVITY_RECURRENCE_UNTIL_BEFORE_START: {
        build: () => m.error_work_activity_recurrence_until_before_start(),
    },
    // Tệp đính kèm của hoạt động công việc (#M-173).
    WORK_ACTIVITY_FILE_REQUIRED: {
        build: () => m.error_work_activity_file_required(),
    },
    WORK_ACTIVITY_FILE_TOO_LARGE: {
        build: () => m.error_work_activity_file_too_large(),
    },
    WORK_ACTIVITY_FILE_UNSUPPORTED_TYPE: {
        build: () => m.error_work_activity_file_unsupported_type(),
    },
    WORK_ACTIVITY_TOO_MANY_FILES: {
        build: () => m.error_work_activity_too_many_files(),
    },
    WORK_ACTIVITY_FILE_NOT_ATTACHABLE: {
        build: () => m.error_work_activity_file_not_attachable(),
    },
    WORK_ACTIVITY_FILE_NOT_FOUND: {
        build: () => m.error_work_activity_file_not_found(),
    },
}

function toText(value: unknown): string {
    if (value === undefined || value === null) return ''
    return String(value)
}

function hasValue(value: unknown): boolean {
    return value !== undefined && value !== null && String(value) !== ''
}

// Resolves a backend error code to its localized message. Returns null when the
// code has no mapping yet, or when a mapped message is missing a required
// param, so callers can fall back without losing the current behaviour.
export function resolveApiErrorMessage(
    code: string | undefined,
    params?: ApiErrorParams,
): string | null {
    if (!code) return null
    const entry = API_ERROR_MESSAGES[code]
    if (!entry) return null
    const values = params ?? {}
    if (entry.required?.some((key) => !hasValue(values[key]))) return null
    return entry.build(values)
}
